import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { FormattedMessage } from "react-intl";
import { useAuth, useLinks, useProfile, useQueryData, useToast } from "hooks";
import { ICON_TYPE } from "components/Icon";
import { GET_NETWORK_PROFILES } from "graphql/queries";
import { getTransformedFilters, NETWORK_LIST_FILTERS_CONFIG } from "constants/index";

/**
 * useNetworkSearch
 */
export default function useNetworkSearch({
  keyword,
  paginationType,
  advancedFilters = {},
  sortKey,
  sortDirection,
  handleSkip = false,
  variables = {},
  filters = {},
  resultsPerPage,
}) {
  const keyName = "networkProfiles";
  const [showAddToListsForm, setShowAddToListsForm] = useState(false);
  const [showInviteMissionForm, setShowInviteMissionForm] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState({});
  const [storedTotalCount, setStoredTotalCount] = useState(0);
  const navigate = useNavigate();
  const { partner, isPartner, isAdmin } = useAuth();
  const links = useLinks();
  const { toast } = useToast();
  const { resendConfirmation } = useProfile();

  const advancedOptions = (row) => [
    {
      label: <FormattedMessage id="Global.Edit" />,
      icon: ICON_TYPE.edit,
      onClick: () => navigate(links.private_profile(row.slug)),
    },
    ...(isPartner
      ? []
      : [
          {
            label: <FormattedMessage id="Lists.ListFavoriteButton.Add" values={{ icon: null }} />,
            icon: ICON_TYPE.plus,
            onClick: () => {
              setSelectedProfile(row);
              setShowAddToListsForm(true);
            },
          },
        ]),
    {
      label: <FormattedMessage id="ProfileAdminBar.InviteMember.Tooltip" />,
      icon: ICON_TYPE.email,
      onClick: () => {
        setSelectedProfile(row);
        setShowInviteMissionForm(true);
      },
    },
    ...(isAdmin && row.user && !row.user.confirmedAt
      ? [
          row.user.confirmationLink && {
            label: <FormattedMessage id="Network.NetworkList.AdvancedOptions.CopyConfirmationLink" />,
            icon: ICON_TYPE.link,
            onClick: async () => {
              // it copies the given text to the clipboard
              await window.navigator.clipboard.writeText(row.user?.confirmationLink);
              toast.success(<FormattedMessage id="Missions.MissionPublicLink.LinkCopied" />);
            },
          },
          {
            label: <FormattedMessage id="Network.NetworkList.AdvancedOptions.ResendConfirmationLink" />,
            icon: ICON_TYPE.email,
            onClick: () => resendConfirmation(row.user?.id),
          },
        ].filter(Boolean)
      : []),
  ];

  const getUserBasedStaticFilters = () => {
    if (isPartner) {
      return {
        profileIds: partner?.profiles.map((profile) => `${profile?.id}`),
      };
    }

    return {};
  };

  const { loading, loadingMore, data, error, handlePageChange } = useQueryData({
    queryName: GET_NETWORK_PROFILES,
    showLog: true,
    keyName,
    fetchPolicy: "cache-and-network",
    resultsPerPage,
    paginationType,
    variables: {
      sortKey,
      sortDirection,
      filters: {
        ...filters,
        ...getUserBasedStaticFilters(),
        keyword,
      },
      advancedFilters: getTransformedFilters(NETWORK_LIST_FILTERS_CONFIG, advancedFilters),
      resultFilterMetadata: Object.keys(NETWORK_LIST_FILTERS_CONFIG),
      ...variables,
    },
    skip: handleSkip,
  });

  const updatedTotalCount = data?.[keyName]?.pageInfo?.totalCount;

  useEffect(() => {
    if (typeof updatedTotalCount !== "undefined") {
      setStoredTotalCount(updatedTotalCount);
    }
  }, [updatedTotalCount]);

  return {
    advancedOptions,
    selectedProfile,
    showAddToListsForm,
    setShowAddToListsForm,
    showInviteMissionForm,
    setShowInviteMissionForm,
    loading,
    loadingMore,
    error,
    handlePageChange,
    data: data?.[keyName]?.networkProfiles,
    pageInfo: data?.[keyName]?.pageInfo,
    currentPage: data?.[keyName]?.pageInfo?.currentPage,
    resultsPerPage: data?.[keyName]?.pageInfo?.perPage,
    totalCount: storedTotalCount,
    filterMetadata: data?.[keyName]?.resultFilterMetadata,
  };
}
